import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Container, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ExtensionCard } from '../../components/extension-card';
import { ExtensionGrid } from '../../components/page-primitives';
import { ListLoadError } from '../../components/list-load-error';
import { useSearch } from '../../hooks/use-search';
import { CURATED_SIZE, DEFAULT_CURATED_SECTIONS, useCuratedRows } from './use-home-data';
const RowFrame = styled('div')({ position: 'relative' });
// Stand-ins a failed row shows, faded, behind its error: they keep the loaded row's height at every width.
const VANISHED_EXTENSIONS = [
    'Language Support',
    'Color Theme',
    'Code Formatter',
    'Linter',
    'Debugger',
    'Snippets',
    'Git Tools'
].map((displayName, idx) => ({
    url: '',
    files: {},
    name: `vanished-${idx}`,
    namespace: 'open-vsx',
    version: '1.0.0',
    allVersions: [],
    deprecated: false,
    displayName,
    description: 'Extends the editor',
    downloadCount: 12000 * (idx + 1),
    averageRating: 4,
    reviewCount: 8 * (idx + 1)
}));
const RowGrid = styled(ExtensionGrid, { shouldForwardProp: prop => prop !== 'vanished' })(({ vanished }) => vanished ? { filter: 'blur(0.1875rem)', opacity: 0.35, pointerEvents: 'none', userSelect: 'none' } : {});
const RowLoadError = styled(ListLoadError)({
    position: 'absolute',
    inset: 0,
    justifyContent: 'center',
    alignContent: 'center',
    padding: '0 1rem',
    textAlign: 'center'
});
/** Renders the curated extension rows (e.g. "Most downloaded"), skipping empty ones. */
export const CuratedSections = props => {
    const rows = useCuratedRows(props.sections ?? DEFAULT_CURATED_SECTIONS);
    const { search } = useSearch();
    return (_jsx(_Fragment, { children: rows.map(row => {
            // A failed refetch keeps the extensions it already had, like the search results.
            const failed = !!row.error && !row.loading && row.extensions.length === 0;
            if (!row.loading && row.extensions.length === 0 && !failed) {
                return null;
            }
            return (_jsxs(Container, { maxWidth: 'xl', component: 'section', "aria-label": row.title, "aria-busy": row.loading, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: '0.875rem'
                        }, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: {
                                            fontSize: { xs: '1rem', sm: '1.4375rem' },
                                            fontWeight: 700,
                                            letterSpacing: '-0.02em'
                                        }, children: row.title }), _jsx(Typography, { component: 'span', sx: {
                                            fontSize: '0.8125rem',
                                            color: 'text.disabled',
                                            display: { xs: 'none', sm: 'block' }
                                        }, children: row.subtitle })] }), _jsx(Box, { component: 'button', onClick: props.onSeeAll ?? (() => search({ query: '', sortBy: row.sortBy })), sx: {
                                    background: 'none',
                                    border: 'none',
                                    color: 'secondary.light',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }, children: "See all \u2192" })] }), _jsxs(RowFrame, { children: [_jsx(RowGrid, { vanished: failed, "aria-hidden": failed || undefined, children: Array.from({ length: row.loading || failed ? CURATED_SIZE : row.extensions.length }, (_, idx) => (_jsx(ExtensionCard, { extension: failed
                                        ? VANISHED_EXTENSIONS[idx % VANISHED_EXTENSIONS.length]
                                        : row.extensions[idx], tabIndex: failed ? -1 : undefined, fadeDelayMs: (idx % CURATED_SIZE) * 200 }, idx))) }), failed && (_jsx(RowLoadError, { message: 'We couldn\u2019t load extensions at this time.', onRetry: row.refetch }))] })] }, row.title));
        }) }));
};
//# sourceMappingURL=curated-sections.js.map